import { memo, useCallback, useRef } from 'react'
import { ActivityIndicator, StyleSheet, useWindowDimensions, View } from 'react-native'
import type RNMapView from 'react-native-maps'
import { Icon, Modal, Portal, Text, useTheme } from 'react-native-paper'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'
import { useTravelMovement } from '@/hooks/useTravelMovement'
import { BREAKPOINTS } from '@/lib/config'
import { formatDateWithWeekday, formatTimeOfDay } from '@/lib/dates'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { Button } from '@/ui/Button'
import MapView from '@/ui/MapView'
import { PathMarkerItem, PathPolyline } from '@/ui/map/PathMarkers'

interface TravelMapModalProps {
    entry: TimeEntriesExpanded | null
    orgSlug: string
    userColor: string
    onDismiss: () => void
}

export const TravelMapModal = memo(function TravelMapModal({
    entry,
    orgSlug,
    userColor,
    onDismiss,
}: TravelMapModalProps) {
    const theme = useTheme()
    const { width, height } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg

    const visible = !!entry
    useDismissOnEscape(visible, onDismiss)
    const userName = entry?.expand?.user?.name ?? 'Unknown User'
    const clockIn = entry?.clock_in ?? ''
    const clockOut = entry?.clock_out ?? clockIn

    const { path, region, isLoading, isEmpty } = useTravelMovement({
        orgSlug,
        userId: entry?.user ?? '',
        clockIn,
        clockOut,
        userColor: userColor || theme.colors.primary,
        userName,
        enabled: visible,
    })

    const mapRef = useRef<RNMapView>(null)

    const onMapReady = useCallback(() => {
        setTimeout(() => {
            if (mapRef.current && path && path.coordinates.length > 0) {
                mapRef.current.fitToCoordinates(path.coordinates, {
                    edgePadding: { top: 40, right: 40, bottom: 40, left: 40 },
                    animated: false,
                })
            }
        }, 0)
    }, [path])

    const modalWidth = isDesktop ? Math.min(width - 40, 800) : width - 40
    const mapHeight = height * 0.6

    if (!visible) return null

    return (
        <Portal>
            <Modal
                visible={visible}
                onDismiss={onDismiss}
                contentContainerStyle={[
                    styles.modalContainer,
                    {
                        backgroundColor: theme.colors.surface,
                        width: modalWidth,
                        maxHeight: height * 0.85,
                    },
                ]}
            >
                <View style={styles.header}>
                    <View style={styles.headerInfo}>
                        <Text variant="titleLarge">Travel Route</Text>
                        <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                            {userName} — {formatDateWithWeekday(clockIn)},{' '}
                            {formatTimeOfDay(clockIn)}
                            {clockOut ? ` – ${formatTimeOfDay(clockOut)}` : ''}
                        </Text>
                    </View>
                    <Button title="✕" onPress={onDismiss} mode="text" />
                </View>

                <View style={[styles.mapContainer, { height: mapHeight }]}>
                    {isLoading ? (
                        <View style={styles.centeredState}>
                            <ActivityIndicator size="large" color={theme.colors.primary} />
                        </View>
                    ) : isEmpty ? (
                        <View style={styles.centeredState}>
                            <Icon
                                source="map-marker-off"
                                size={48}
                                color={theme.colors.onSurfaceVariant}
                            />
                            <Text
                                variant="bodyLarge"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                No movement data available
                            </Text>
                            <Text
                                variant="bodySmall"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                No location points were recorded during this travel period.
                            </Text>
                        </View>
                    ) : (
                        <MapView
                            ref={mapRef}
                            style={styles.map}
                            initialRegion={region}
                            onMapReady={onMapReady}
                            showsUserLocation={false}
                            showsMyLocationButton={false}
                            showsCompass={false}
                            showsTraffic={false}
                            showsBuildings={false}
                            showsIndoors={false}
                            toolbarEnabled={false}
                        >
                            {path && (
                                <>
                                    <PathPolyline
                                        path={path}
                                        focusOpacity={1}
                                        referenceTime={new Date()}
                                    />
                                    {path.pathMarkers.map(marker => (
                                        <PathMarkerItem key={marker.id} marker={marker} />
                                    ))}
                                </>
                            )}
                        </MapView>
                    )}
                </View>
            </Modal>
        </Portal>
    )
})

const styles = StyleSheet.create({
    modalContainer: {
        margin: 20,
        borderRadius: 12,
        alignSelf: 'center',
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingHorizontal: 24,
        paddingTop: 20,
        paddingBottom: 12,
    },
    headerInfo: {
        flex: 1,
        gap: 4,
    },
    mapContainer: {
        overflow: 'hidden',
    },
    map: {
        flex: 1,
    },
    centeredState: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        padding: 24,
    },
})
